import React, { useEffect, useState } from "react";
import axios from "../utils/axiosConfig";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { FaCheck, FaStar, FaCrown, FaRocket, FaBolt } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import { useNavigate } from 'react-router-dom';

interface PointsPackage {
  id: number;
  name: string;
  points: number;
  price: number;
  discount: number;
  description?: string;
}

// Icons for different package tiers
const packageIcons = {
  1: FaBolt,
  2: HiSparkles,
  3: FaStar,
  4: FaCrown,
  5: FaRocket,
  6: FaCrown,
};

// Determine if a package is "most popular" (you can adjust this logic)
const getMostPopularId = (packages: PointsPackage[]) => {
  // Usually the middle-tier package or the one with best value
  if (packages.length >= 3) {
    return packages[2].id; // Third package (Ultimate Pack)
  }
  return packages[1]?.id || packages[0]?.id;
};

export function PricePage() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState<PointsPackage[]>([]);
  const [loading, setLoading] = useState<number | null>(null);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get("/points-packages");
        setPackages(response.data.packages);
      } catch (error) {
        console.error("Error loading points packages:", error);
      }
    };

    fetchPackages();
  }, []);

  const handleBuyClick = async (pkg: PointsPackage) => {
    setLoading(pkg.id);
    setDisabled(true);
    try {
      const response = await axios.get(`/payment/link/${pkg.id}`);

      if (response.status === 200 && response.data.payment_url) {
        window.location.href = response.data.payment_url;
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error creating payment link:", error);
    } finally {
      setLoading(null);
      setDisabled(false);
    }
  };

  const mostPopularId = getMostPopularId(packages);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-divine bg-clip-text text-transparent">
            Choose Your Plan
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Select the perfect plan for your translation and voiceover needs.
            All plans include unlimited translations with premium AI voices.
          </p>
        </div>

        {/* Main Packages Row */}
        <div className="mb-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {packages
              .filter((pkg) => pkg.id <= 4)
              .map((pkg) => {
                const IconComponent =
                  packageIcons[pkg.id as keyof typeof packageIcons] ||
                  HiSparkles;
                const isMostPopular = pkg.id === mostPopularId;
                const isEnterprise = false; // Main packages are not enterprise

                return (
                  <Card
                    key={pkg.id}
                    className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02] group min-h-[480px] ${
                      isMostPopular
                        ? "border-divine-purple shadow-lg ring-2 ring-divine-purple/20"
                        : "hover:border-divine-purple/50"
                    } ${
                      isEnterprise
                        ? "bg-gradient-to-br from-divine-darkPurple to-divine-secondary text-divine-lightPurple"
                        : ""
                    }`}
                  >
                    {/* Most Popular Corner Badge */}
                    {isMostPopular && (
                      <div className="absolute top-0 left-0 z-20">
                        <div className="bg-gradient-divine text-white px-4 py-2 text-xs font-semibold shadow-lg rounded-br-lg rounded-tl-lg">
                          <FaStar className="w-3 h-3 mr-1 inline" />
                          Most Popular
                        </div>
                      </div>
                    )}

                    {/* Discount Badge */}
                    {pkg.discount > 0 && (
                      <div className="absolute top-4 right-4 z-10">
                        <Badge className="bg-destructive text-destructive-foreground text-xs font-bold px-3 py-1">
                          {pkg.discount}% OFF
                        </Badge>
                      </div>
                    )}

                    <CardHeader className="text-center pb-4 pt-6">
                      <div
                        className={`inline-flex items-center justify-center w-14 h-14 rounded-full mb-6 ${
                          isEnterprise
                            ? "bg-divine-gold/20"
                            : "bg-divine-purple/10"
                        }`}
                      >
                        <IconComponent
                          className={`w-7 h-7 ${
                            isEnterprise
                              ? "text-divine-gold"
                              : "text-divine-purple"
                          }`}
                        />
                      </div>

                      <CardTitle
                        className={`text-xl lg:text-2xl font-bold mb-4 ${
                          isEnterprise ? "text-divine-gold" : ""
                        }`}
                      >
                        {pkg.name}
                      </CardTitle>

                      <div className="space-y-2 mb-6">
                        <div className="text-3xl lg:text-4xl font-bold">
                          ${pkg.price}
                        </div>
                        <CardDescription
                          className={`text-sm ${
                            isEnterprise
                              ? "text-divine-lightPurple/80"
                              : "text-muted-foreground"
                          }`}
                        >
                          {pkg.points.toLocaleString()} points
                        </CardDescription>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-6 px-6 pb-4">
                      {/* Features List */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 text-sm">
                          <FaCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span
                            className={
                              isEnterprise ? "text-divine-lightPurple" : ""
                            }
                          >
                            ~{Math.round(pkg.points / 1.6).toLocaleString()}{" "}
                            words
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <FaCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span
                            className={
                              isEnterprise ? "text-divine-lightPurple" : ""
                            }
                          >
                            Premium AI voices
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <FaCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span
                            className={
                              isEnterprise ? "text-divine-lightPurple" : ""
                            }
                          >
                            Multiple languages
                          </span>
                        </div>
                        {isEnterprise && (
                          <div className="flex items-center gap-3 text-sm">
                            <FaCheck className="w-4 h-4 text-divine-gold flex-shrink-0" />
                            <span className="text-divine-gold font-medium">
                              Priority support
                            </span>
                          </div>
                        )}
                      </div>

                      {/* CTA Button */}
                      <div className="mt-8">
                        <Button
                          onClick={() => handleBuyClick(pkg)}
                          disabled={disabled}
                          className={`w-full font-semibold transition-all duration-200 h-12 px-6 text-base ${
                            isMostPopular
                              ? "bg-gradient-divine hover:opacity-90 text-white shadow-lg"
                              : isEnterprise
                              ? "bg-divine-gold hover:bg-divine-gold/90 text-divine-darkPurple"
                              : "bg-primary hover:bg-primary/90"
                          }`}
                        >
                          {loading === pkg.id ? (
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              Processing...
                            </div>
                          ) : (
                            `Get ${pkg.name}`
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </div>

        {/* Enterprise Packages Row */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-2">Enterprise Plans</h3>
            <p className="text-muted-foreground">
              For teams and organizations with higher volume needs
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8 max-w-2xl mx-auto">
            {packages
              .filter((pkg) => pkg.id >= 5)
              .map((pkg) => {
                const IconComponent =
                  packageIcons[pkg.id as keyof typeof packageIcons] ||
                  HiSparkles;
                const isMostPopular = false; // Enterprise packages don't have "most popular"
                const isEnterprise = true; // These are enterprise packages

                return (
                  <Card
                    key={pkg.id}
                    className={`relative overflow-visible transition-all duration-300 hover:shadow-xl hover:scale-[1.02] group min-h-[480px] bg-gradient-to-br from-divine-darkPurple to-divine-secondary text-divine-lightPurple`}
                  >
                    {/* Discount Badge */}
                    {pkg.discount > 0 && (
                      <div className="absolute top-6 right-6 z-10">
                        <Badge className="bg-destructive text-destructive-foreground text-xs font-bold px-3 py-1">
                          {pkg.discount}% OFF
                        </Badge>
                      </div>
                    )}

                    <CardHeader className="text-center pb-4 pt-6">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-6 bg-divine-gold/20">
                        <IconComponent className="w-7 h-7 text-divine-gold" />
                      </div>

                      <CardTitle className="text-xl lg:text-2xl font-bold mb-4 text-divine-gold">
                        {pkg.name}
                      </CardTitle>

                      <div className="space-y-2 mb-6">
                        <div className="text-3xl lg:text-4xl font-bold">
                          ${pkg.price}
                        </div>
                        <CardDescription className="text-sm text-divine-lightPurple/80">
                          {pkg.points.toLocaleString()} points
                        </CardDescription>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-6 px-6 pb-4">
                      {/* Features List */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 text-sm">
                          <FaCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-divine-lightPurple">
                            ~{Math.round(pkg.points / 1.6).toLocaleString()}{" "}
                            words
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <FaCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-divine-lightPurple">
                            Premium AI voices
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <FaCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-divine-lightPurple">
                            Multiple languages
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <FaCheck className="w-4 h-4 text-divine-gold flex-shrink-0" />
                          <span className="text-divine-gold font-medium">
                            Priority support
                          </span>
                        </div>
                      </div>

                      {/* CTA Button */}
                      <div className="mt-8">
                        <Button
                          onClick={() => handleBuyClick(pkg)}
                          disabled={disabled}
                          className="w-full font-semibold transition-all duration-200 h-12 px-6 text-base bg-divine-gold hover:bg-divine-gold/90 text-divine-darkPurple"
                        >
                          {loading === pkg.id ? (
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              Processing...
                            </div>
                          ) : (
                            `Get ${pkg.name}`
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-card rounded-2xl p-6 lg:p-8 border border-border/50 shadow-lg">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-4 text-foreground">
                Why Choose Points?
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <FaCheck className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Better Value</h3>
                    <p className="text-sm text-muted-foreground">
                      Save more with our discounted points packages compared to
                      per-task pricing.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <FaBolt className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Seamless Experience</h3>
                    <p className="text-sm text-muted-foreground">
                      No more payment interruptions. Points are automatically
                      deducted for each task.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <HiSparkles className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Flexible Usage</h3>
                    <p className="text-sm text-muted-foreground">
                      Use points for both translations and voiceovers. Perfect
                      for any project size.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-divine-purple/10 to-divine-secondary/10 rounded-xl p-6 border border-divine-purple/20">
              <h3 className="text-xl font-bold mb-4 text-divine-purple">
                How It Works
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-divine-purple text-white flex items-center justify-center text-xs font-bold">
                    1
                  </div>
                  <span>Choose your points package</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-divine-purple text-white flex items-center justify-center text-xs font-bold">
                    2
                  </div>
                  <span>Upload your documents or text</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-divine-purple text-white flex items-center justify-center text-xs font-bold">
                    3
                  </div>
                  <span>Points are automatically deducted</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-divine-purple text-white flex items-center justify-center text-xs font-bold">
                    4
                  </div>
                  <span>Get your translations & voiceovers</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Become a Sponsor Section */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-8">
            <h2 className="text-2xl font-bold mb-4 font-['Playfair_Display']">
              Help Others Spread the Gospel
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-6 text-lg">
              Support fellow believers by sponsoring their translation and voice generation needs. 
              Help quality evangelical content reach more languages worldwide.
            </p>
            <Button 
              size="lg"
              onClick={() => navigate('/sponsorship')}
              className="px-8 py-3 text-lg"
            >
              Become a Sponsor
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
